/*!
 * outputLatency.js — how late this machine's speakers are heard
 * © 2026 Rob Canning — GPLv3
 *
 * The beat grid is laid onto the audio clock as heard (synth.js,
 * getOutputTimestamp), so what the browser knows of its own output delay is
 * allowed for already. What it does not know -- Bluetooth speakers, an
 * interface's own buffer, a PA's processing, the hall -- is this: a number
 * of ms, this machine's own (kept in localStorage, never in the project),
 * by which every note is scheduled earlier, so it is heard on the beat.
 * Set with latency(ms) in the console, or Preferences > UI.
 */

const KEY = "oscilla.outputLatencyMs";
const MAX = 1000;

const clamp = v => Math.max(0, Math.min(MAX, Number(v) || 0));

function read() {
  try { return clamp(localStorage.getItem(KEY)); } catch { return 0; }
}

let latency = read();

/** This machine's extra output latency, ms. */
export const outputLatencyMs = () => latency;

/** Set it (0..1000 ms), kept for this browser; the grid is laid again. Returns it. */
export function setOutputLatencyMs(ms) {
  latency = clamp(ms);
  try { localStorage.setItem(KEY, String(latency)); } catch { /* private mode: for this page only */ }
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") window.dispatchEvent(new CustomEvent("oscilla:output-latency", { detail: latency }));
  return latency;
}

/** What the browser says of its own output delay, ms, or null (already allowed for). */
export function browserOutputLatencyMs(ctx = globalThis.window?.sharedAudioCtx) {
  const s = Number(ctx?.outputLatency ?? ctx?.baseLatency);
  return Number.isFinite(s) && s > 0 ? Math.round(s * 1000) : null;
}
